import type { RouteObject } from "react-router";

import {
  Alert,
  AlertKind,
  Badge,
  BadgeColor,
  PageHeading,
  Spinner,
  TableChartIcon,
  WavesIcon,
} from "@prequel/shared-components";

import AuditLogs from "./AuditLogs";
import JobsCard from "./JobsCard";
import ResourcesCard from "./ResourcesCard";

import { useImportOverview } from "@/api/import/overview";
import Breadcrumbs from "@/components/Breadcrumbs";

const OVERVIEW_WINDOW_DAYS = 7;

const OverviewLayout = () => {
  const { data: overview, isLoading, error } = useImportOverview(OVERVIEW_WINDOW_DAYS);

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Overview" breadcrumbs={<Breadcrumbs />} />

        <div className="mb-4 flex justify-end">
          <Badge text={`Last ${OVERVIEW_WINDOW_DAYS} days`} color={BadgeColor.GRAY} />
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        )}

        {error && <Alert kind={AlertKind.ERROR}>Failed to load the import overview.</Alert>}

        {overview && (
          <div
            className="
              mb-4 grid grid-cols-1 gap-4
              lg:grid-cols-2
            "
          >
            <JobsCard
              title="Extracts overview"
              counts={overview.extracts}
              activityPath="/import/activity?type=extract"
              emptyMessage="No extracts in this window."
            />
            <JobsCard
              title="Loads overview"
              counts={overview.loads}
              activityPath="/import/activity?type=load"
              emptyMessage="No loads in this window."
            />
            <ResourcesCard
              title="Datasets"
              icon={TableChartIcon}
              group={overview.datasets}
              listPath="/import/datasets"
              emptyMessage="No datasets configured."
            />
            <ResourcesCard
              title="Streams"
              icon={WavesIcon}
              group={overview.streams}
              listPath="/import/streams"
              emptyMessage="No streams configured."
            />
          </div>
        )}

        <AuditLogs />
      </div>
    </div>
  );
};

export const overviewRouteChildren: RouteObject = {
  path: "overview",
  handle: {
    breadcrumb: () => ({ label: "Overview" }),
  },
  element: <OverviewLayout />,
};
